import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import FrequencyDropdown from 'report-customization-components/components/frequency-dropdown/FrequencyDropdown';
import { REPORT_CUSTOMIZATION_COMPONENT_IDS } from 'report-customization-components/constants/report-customization-components/componentTypes';
import { getIsReportValidForCustomizationComponentId } from 'report-customization-components/lib/reportCustomizationComponentFetchers';
import { getFrequency, getReportConfig } from 'reporting-data/report/configReportGetters';
import { isConfigReport, isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import { getUsageTracker } from 'reporting-ui-components/monitoring/usageTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import { SRV_REPORT_FREQUENCY_DROPDOWN_GATE } from '../../constants/gates';
import { useGate } from '../../hooks/useGate';
import { ReportOptionsCategories } from '../singleReportViewer/fullPage/sidebar/categorized/ReportOptionCategory';
import BaseSelectEditorComponent from './common/BaseSelectEditorComponent';
import { getValidFrequenciesForReport } from './common/dateRangeHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
export const getFrequencyOptions = report => getValidFrequenciesForReport(report).map(value => ({
  value,
  text: I18n.text(`settingLabels.frequency.valueLabels.${value}`)
}));
const FrequencyEditor = ({
  isV2FilterExperience,
  onChange,
  options: {
    readOnly
  },
  report,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName
}) => {
  const usageTracker = getUsageTracker();
  const isUngatedForSRVReportFrequencyDropdown = useGate(SRV_REPORT_FREQUENCY_DROPDOWN_GATE);
  const frequency = isConfigReport(report) && getFrequency(getReportConfig(report));
  const options = useMemo(() => getFrequencyOptions(report), [report]);
  const handleOnChange = useCallback(({
    target: {
      value: newFrequency
    }
  }) => {
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.FREQUENCY,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    let updatedReport = report.setIn(['config', 'frequency'], newFrequency);
    if (isMultiConfigReport(updatedReport)) {
      updatedReport = updatedReport.update('reportConfigs', configs => {
        if (isMultiConfigReport(report)) {
          return configs.map(config => config.set('frequency', newFrequency));
        }
        return configs;
      });
    }
    onChange(updatedReport);
  }, [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  const onChangeForFrequencyDropdown = useCallback(updatedReport => {
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.FREQUENCY,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    onChange(updatedReport);
  }, [isV2FilterExperience, onChange, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settingLabels.frequency.label"
  });
  if (isV2FilterExperience) {
    return /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      "data-test-id": "frequency-editor",
      tooltipHeader: label,
      disabled: readOnly,
      onChange: handleOnChange,
      options: options,
      value: frequency
    });
  }
  if (isUngatedForSRVReportFrequencyDropdown) {
    return /*#__PURE__*/_jsx(FrequencyDropdown, {
      report: report,
      onChange: onChangeForFrequencyDropdown
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "settingLabels.frequency.label"
    }),
    children: /*#__PURE__*/_jsx(Select, {
      "data-test-id": "frequency-editor",
      disabled: readOnly,
      value: frequency,
      options: options,
      onChange: handleOnChange
    })
  });
};
FrequencyEditor.isUsableForReport = (report, gates, scopes) => {
  return getIsReportValidForCustomizationComponentId(REPORT_CUSTOMIZATION_COMPONENT_IDS.FREQUENCY_DROPDOWN, report,
  /**
   * we are going to deprecate this system of components in the future. So until then, we can do this hacky thing to
   * allow the frequency dropdown to get scopes/gates.
   * */
  {
    gates,
    user: {
      scopes
    }
  });
};
FrequencyEditor.isDateEditor = true;
FrequencyEditor.category = ReportOptionsCategories.Group;
FrequencyEditor.propTypes = {
  appUsageTracker: PropTypes.object,
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({
    readOnly: PropTypes.bool
  }).isRequired
};
export default FrequencyEditor;